import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Navigation } from '../Navigation';
import {
  algoService,
  AlgoEngine, AlgoPolicy, CreateEngineRequest, ExecutionRules,
  ScheduleOption, StrategyId, RiskAppetite, ConditionOverride, AiMode,
  STRATEGIES, RISK_LABELS, SCHEDULE_LABELS,
} from '../../services/algoService';
import { canAccessAutoTrader } from '../../lib/entitlements';
import '../Dashboard.css';
import { RiskDisclaimer } from '../shared/RiskDisclaimer';

// ─── Indicator metadata for condition editor ─────────────────────────────────

interface IndicatorOption { value: string; label: string; description: string; category: string }

const INDICATOR_OPTIONS: IndicatorOption[] = [
  // Technical — Price & Momentum
  { value: 'rsi', label: 'RSI (14)', description: '0-100 — below 30 oversold, above 70 overbought', category: 'Technical' },
  { value: 'macd_histogram', label: 'MACD Histogram', description: 'Positive = bullish momentum, negative = bearish', category: 'Technical' },
  { value: 'bb_position', label: 'BB Position', description: '0 = lower band, 1 = upper band', category: 'Technical' },
  { value: 'volume_ratio', label: 'Volume Ratio', description: 'Current volume / 20-day avg (1.5 = 50% above avg)', category: 'Technical' },
  { value: 'atr_pct', label: 'ATR %', description: 'Average True Range as % of price (volatility)', category: 'Technical' },

  // Technical — Trend
  { value: 'price_vs_sma20', label: 'Price vs SMA20', description: 'Ratio — 1.02 = 2% above SMA20', category: 'Trend' },
  { value: 'sma20_vs_sma50', label: 'SMA20 vs SMA50', description: 'Ratio — above 1 = golden cross', category: 'Trend' },
  { value: 'price_vs_20d_high', label: 'Price vs 20d High', description: 'Ratio — 1.0 = at high, 0.95 = 5% below', category: 'Trend' },
  { value: 'price_vs_20d_low', label: 'Price vs 20d Low', description: 'Ratio — 1.0 = at low, 1.10 = 10% above', category: 'Trend' },
  { value: 'avwap_distance', label: 'AVWAP Distance %', description: 'Positive = above institutional VWAP', category: 'Trend' },

  // Fundamentals (fetched from FMP)
  { value: 'pe_ratio', label: 'P/E Ratio', description: 'Price to earnings — lower = cheaper', category: 'Fundamental' },
  { value: 'debt_equity', label: 'Debt/Equity', description: 'Below 1 = more equity than debt', category: 'Fundamental' },
  { value: 'roe', label: 'ROE %', description: 'Return on equity — higher = more efficient', category: 'Fundamental' },
  { value: 'current_ratio', label: 'Current Ratio', description: 'Above 1 = can cover short-term debts', category: 'Fundamental' },
  { value: 'dividend_yield', label: 'Dividend Yield %', description: 'Annual dividend / price', category: 'Fundamental' },
  { value: 'market_cap_b', label: 'Market Cap ($B)', description: 'Company size in billions', category: 'Fundamental' },
  { value: 'price_change_1m', label: '1-Month Price Change %', description: '30-day price performance', category: 'Fundamental' },
  { value: 'price_change_3m', label: '3-Month Price Change %', description: '90-day price performance', category: 'Fundamental' },

  // Market Conditions (cached, shared across symbols)
  { value: 'vix', label: 'VIX Level', description: 'Market fear — below 20 calm, above 30 volatile', category: 'Market' },
  { value: 'spy_rsi', label: 'SPY RSI', description: 'S&P 500 momentum (0-100)', category: 'Market' },
  { value: 'spy_trend', label: 'SPY Trend', description: '1 = above SMA50 (bull), 0 = below (bear)', category: 'Market' },

  // Sentiment
  { value: 'sentiment_score', label: 'Sentiment Score', description: '0-1 — aggregated news sentiment', category: 'Sentiment' },
];

const OPERATOR_OPTIONS: { value: string; label: string }[] = [
  { value: 'gt', label: '>' },
  { value: 'gte', label: '>=' },
  { value: 'lt', label: '<' },
  { value: 'lte', label: '<=' },
  { value: 'between', label: 'Between' },
];

// Group indicators by category for optgroup rendering
const INDICATOR_CATEGORIES = Array.from(new Set(INDICATOR_OPTIONS.map(o => o.category)));

// ─── Professional Strategy Presets ──────────────────────────────────────────
// Based on institutional and quantitative trading approaches

interface StrategyPreset {
  name: string;
  description: string;
  direction: 'long' | 'short' | 'auto';
  conditions: ConditionOverride[];
}

const STRATEGY_PRESETS: StrategyPreset[] = [
  {
    name: 'CANSLIM Growth',
    description: "William O'Neil's growth stock method — strong earnings, institutional buying, price near highs",
    direction: 'long',
    conditions: [
      { indicator: 'rsi', operator: 'between', min: 50, max: 80, weight: 0.15, label: 'RSI (14)' },
      { indicator: 'price_vs_20d_high', operator: 'gte', value: 0.95, weight: 0.20, label: 'Price vs 20d High' },
      { indicator: 'volume_ratio', operator: 'gte', value: 1.3, weight: 0.20, label: 'Volume Ratio' },
      { indicator: 'sma20_vs_sma50', operator: 'gt', value: 1.0, weight: 0.15, label: 'SMA20 vs SMA50' },
      { indicator: 'roe', operator: 'gte', value: 15, weight: 0.15, label: 'ROE %' },
      { indicator: 'price_change_3m', operator: 'gt', value: 5, weight: 0.15, label: '3-Month Price Change %' },
    ],
  },
  {
    name: 'Value + Momentum',
    description: "Cliff Asness / AQR style — undervalued stocks with improving momentum",
    direction: 'long',
    conditions: [
      { indicator: 'pe_ratio', operator: 'between', min: 5, max: 20, weight: 0.20, label: 'P/E Ratio' },
      { indicator: 'debt_equity', operator: 'lt', value: 1.5, weight: 0.15, label: 'Debt/Equity' },
      { indicator: 'rsi', operator: 'between', min: 45, max: 70, weight: 0.15, label: 'RSI (14)' },
      { indicator: 'macd_histogram', operator: 'gt', value: 0, weight: 0.20, label: 'MACD Histogram' },
      { indicator: 'price_vs_sma20', operator: 'gt', value: 1.0, weight: 0.15, label: 'Price vs SMA20' },
      { indicator: 'current_ratio', operator: 'gte', value: 1.2, weight: 0.15, label: 'Current Ratio' },
    ],
  },
  {
    name: 'Mean Reversion',
    description: "Buy oversold quality stocks — RSI washed out, strong fundamentals, below VWAP",
    direction: 'long',
    conditions: [
      { indicator: 'rsi', operator: 'lt', value: 35, weight: 0.25, label: 'RSI (14)' },
      { indicator: 'bb_position', operator: 'lt', value: 0.15, weight: 0.20, label: 'BB Position' },
      { indicator: 'avwap_distance', operator: 'lt', value: -2, weight: 0.15, label: 'AVWAP Distance %' },
      { indicator: 'roe', operator: 'gte', value: 10, weight: 0.15, label: 'ROE %' },
      { indicator: 'volume_ratio', operator: 'gte', value: 1.5, weight: 0.15, label: 'Volume Ratio' },
      { indicator: 'spy_trend', operator: 'gte', value: 1, weight: 0.10, label: 'SPY Trend' },
    ],
  },
  {
    name: 'Trend Following (Turtle)',
    description: "Richard Dennis turtle method — ride strong trends with confirmation",
    direction: 'auto',
    conditions: [
      { indicator: 'price_vs_20d_high', operator: 'gte', value: 0.98, weight: 0.25, label: 'Price vs 20d High' },
      { indicator: 'sma20_vs_sma50', operator: 'gt', value: 1.0, weight: 0.20, label: 'SMA20 vs SMA50' },
      { indicator: 'atr_pct', operator: 'between', min: 1.5, max: 5, weight: 0.15, label: 'ATR %' },
      { indicator: 'volume_ratio', operator: 'gte', value: 1.2, weight: 0.15, label: 'Volume Ratio' },
      { indicator: 'rsi', operator: 'between', min: 50, max: 75, weight: 0.15, label: 'RSI (14)' },
      { indicator: 'macd_histogram', operator: 'gt', value: 0, weight: 0.10, label: 'MACD Histogram' },
    ],
  },
  {
    name: 'Dividend Compounder',
    description: "Buffett-style — profitable dividend payers at reasonable valuations with uptrend",
    direction: 'long',
    conditions: [
      { indicator: 'dividend_yield', operator: 'gte', value: 1.5, weight: 0.20, label: 'Dividend Yield %' },
      { indicator: 'pe_ratio', operator: 'between', min: 8, max: 25, weight: 0.20, label: 'P/E Ratio' },
      { indicator: 'roe', operator: 'gte', value: 12, weight: 0.15, label: 'ROE %' },
      { indicator: 'debt_equity', operator: 'lt', value: 2.0, weight: 0.15, label: 'Debt/Equity' },
      { indicator: 'price_vs_sma20', operator: 'gt', value: 1.0, weight: 0.15, label: 'Price vs SMA20' },
      { indicator: 'rsi', operator: 'between', min: 35, max: 65, weight: 0.15, label: 'RSI (14)' },
    ],
  },
  {
    name: 'Volatility Breakout',
    description: "Mark Minervini SEPA — low volatility compression followed by expansion with volume",
    direction: 'long',
    conditions: [
      { indicator: 'atr_pct', operator: 'between', min: 2, max: 6, weight: 0.15, label: 'ATR %' },
      { indicator: 'bb_position', operator: 'gte', value: 0.85, weight: 0.20, label: 'BB Position' },
      { indicator: 'volume_ratio', operator: 'gte', value: 1.8, weight: 0.25, label: 'Volume Ratio' },
      { indicator: 'price_vs_20d_high', operator: 'gte', value: 0.97, weight: 0.20, label: 'Price vs 20d High' },
      { indicator: 'rsi', operator: 'between', min: 55, max: 80, weight: 0.10, label: 'RSI (14)' },
      { indicator: 'sma20_vs_sma50', operator: 'gt', value: 1.0, weight: 0.10, label: 'SMA20 vs SMA50' },
    ],
  },
  {
    name: 'Short Squeeze Setup',
    description: "High short interest candidates showing reversal signs — oversold with volume surge",
    direction: 'long',
    conditions: [
      { indicator: 'rsi', operator: 'between', min: 25, max: 45, weight: 0.20, label: 'RSI (14)' },
      { indicator: 'volume_ratio', operator: 'gte', value: 2.0, weight: 0.25, label: 'Volume Ratio' },
      { indicator: 'bb_position', operator: 'lt', value: 0.20, weight: 0.15, label: 'BB Position' },
      { indicator: 'macd_histogram', operator: 'gt', value: 0, weight: 0.20, label: 'MACD Histogram' },
      { indicator: 'price_change_1m', operator: 'lt', value: -10, weight: 0.20, label: '1-Month Price Change %' },
    ],
  },
  {
    name: 'Bear Market Profiter',
    description: "Short overextended stocks in weak markets — high VIX, broken trends, distribution",
    direction: 'short',
    conditions: [
      { indicator: 'rsi', operator: 'between', min: 35, max: 55, weight: 0.15, label: 'RSI (14)' },
      { indicator: 'sma20_vs_sma50', operator: 'lt', value: 1.0, weight: 0.20, label: 'SMA20 vs SMA50' },
      { indicator: 'macd_histogram', operator: 'lt', value: 0, weight: 0.20, label: 'MACD Histogram' },
      { indicator: 'vix', operator: 'gte', value: 22, weight: 0.15, label: 'VIX Level' },
      { indicator: 'volume_ratio', operator: 'gte', value: 1.3, weight: 0.15, label: 'Volume Ratio' },
      { indicator: 'avwap_distance', operator: 'lt', value: -1, weight: 0.15, label: 'AVWAP Distance %' },
    ],
  },
  {
    name: 'Large Cap Quality',
    description: "Institutional blue-chip filter — mega caps with strong balance sheets in uptrends",
    direction: 'long',
    conditions: [
      { indicator: 'market_cap_b', operator: 'gte', value: 50, weight: 0.15, label: 'Market Cap ($B)' },
      { indicator: 'roe', operator: 'gte', value: 15, weight: 0.15, label: 'ROE %' },
      { indicator: 'debt_equity', operator: 'lt', value: 1.0, weight: 0.15, label: 'Debt/Equity' },
      { indicator: 'pe_ratio', operator: 'between', min: 10, max: 35, weight: 0.15, label: 'P/E Ratio' },
      { indicator: 'price_vs_sma20', operator: 'gt', value: 1.0, weight: 0.20, label: 'Price vs SMA20' },
      { indicator: 'rsi', operator: 'between', min: 40, max: 70, weight: 0.10, label: 'RSI (14)' },
      { indicator: 'sentiment_score', operator: 'gte', value: 0.3, weight: 0.10, label: 'Sentiment Score' },
    ],
  },
  {
    name: 'Calm Market Scalper',
    description: "Low-VIX range trading — stable markets, mean reversion plays on minor pullbacks",
    direction: 'long',
    conditions: [
      { indicator: 'vix', operator: 'lt', value: 18, weight: 0.20, label: 'VIX Level' },
      { indicator: 'rsi', operator: 'between', min: 35, max: 50, weight: 0.20, label: 'RSI (14)' },
      { indicator: 'bb_position', operator: 'between', min: 0.10, max: 0.35, weight: 0.20, label: 'BB Position' },
      { indicator: 'atr_pct', operator: 'lt', value: 2.5, weight: 0.15, label: 'ATR %' },
      { indicator: 'price_vs_sma20', operator: 'between', min: 0.97, max: 1.0, weight: 0.15, label: 'Price vs SMA20' },
      { indicator: 'spy_trend', operator: 'gte', value: 1, weight: 0.10, label: 'SPY Trend' },
    ],
  },
];

const ConditionEditor: React.FC<{
  conditions: ConditionOverride[];
  onChange: (conditions: ConditionOverride[]) => void;
  isCustomStrategy?: boolean;
  onDirectionChange?: (dir: 'long' | 'short' | 'auto') => void;
}> = ({ conditions, onChange, isCustomStrategy, onDirectionChange }) => {
  const inputStyle: React.CSSProperties = {
    padding: '6px 10px', border: '1px solid #374151',
    borderRadius: 4, background: '#1f2937', color: '#f9fafb',
    fontSize: 13, boxSizing: 'border-box' as const,
  };

  const addCondition = () => {
    onChange([...conditions, {
      indicator: 'rsi', operator: 'between', min: 30, max: 70, weight: 0.20,
      label: 'RSI (14)',
    }]);
  };

  const updateCondition = (idx: number, field: string, val: string | number) => {
    const updated = conditions.map((c, i) => {
      if (i !== idx) return c;
      const copy = { ...c, [field]: val };
      if (field === 'indicator') {
        const opt = INDICATOR_OPTIONS.find(o => o.value === val);
        copy.label = opt ? opt.label : String(val);
      }
      if (field === 'operator' && val !== 'between') {
        delete copy.min;
        delete copy.max;
      }
      return copy;
    });
    onChange(updated);
  };

  const removeCondition = (idx: number) => {
    onChange(conditions.filter((_, i) => i !== idx));
  };

  const getDescription = (indicator: string) => {
    return INDICATOR_OPTIONS.find(o => o.value === indicator)?.description || '';
  };

  const loadPreset = (preset: StrategyPreset) => {
    onChange([...preset.conditions]);
    if (onDirectionChange) onDirectionChange(preset.direction);
  };

  return (
    <div>
      {isCustomStrategy && (
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 8 }}>Load a preset strategy:</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {STRATEGY_PRESETS.map(preset => (
              <button key={preset.name} onClick={() => loadPreset(preset)}
                title={preset.description}
                style={{
                  padding: '4px 10px', fontSize: 11, borderRadius: 4, cursor: 'pointer',
                  background: preset.direction === 'short' ? '#7f1d1d' : preset.direction === 'auto' ? '#1e3a5f' : '#064e3b',
                  color: preset.direction === 'short' ? '#fca5a5' : preset.direction === 'auto' ? '#93c5fd' : '#6ee7b7',
                  border: '1px solid rgba(255,255,255,0.1)',
                }}>
                {preset.name}
              </button>
            ))}
          </div>
        </div>
      )}
      {isCustomStrategy && conditions.length === 0 && (
        <p style={{ color: '#f59e0b', fontSize: 12, margin: '0 0 8px' }}>
          Custom strategy requires at least one entry condition. Choose a preset above or add conditions manually.
        </p>
      )}
      {conditions.map((cond, idx) => (
        <div key={idx} style={{ marginBottom: 10, padding: '8px 10px', background: 'rgba(255,255,255,0.03)', borderRadius: 6, border: '1px solid #1f2937' }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <select style={{ ...inputStyle, width: 170 }} value={cond.indicator}
              onChange={e => updateCondition(idx, 'indicator', e.target.value)}>
              {INDICATOR_CATEGORIES.map(cat => (
                <optgroup key={cat} label={`── ${cat} ──`}>
                  {INDICATOR_OPTIONS.filter(o => o.category === cat).map(o => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </optgroup>
              ))}
            </select>
            <select style={{ ...inputStyle, width: 80 }} value={cond.operator}
              onChange={e => updateCondition(idx, 'operator', e.target.value)}>
              {OPERATOR_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            {cond.operator === 'between' ? (
              <>
                <input style={{ ...inputStyle, width: 65 }} type="number" step="any" placeholder="Min"
                  value={cond.min ?? ''} onChange={e => updateCondition(idx, 'min', parseFloat(e.target.value) || 0)} />
                <span style={{ color: '#6b7280', fontSize: 12 }}>to</span>
                <input style={{ ...inputStyle, width: 65 }} type="number" step="any" placeholder="Max"
                  value={cond.max ?? ''} onChange={e => updateCondition(idx, 'max', parseFloat(e.target.value) || 0)} />
              </>
            ) : (
              <input style={{ ...inputStyle, width: 75 }} type="number" step="any" placeholder="Value"
                value={cond.value ?? ''} onChange={e => updateCondition(idx, 'value', parseFloat(e.target.value) || 0)} />
            )}
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <span style={{ color: '#6b7280', fontSize: 11 }}>Weight:</span>
              <input style={{ ...inputStyle, width: 55 }} type="number" step="0.05" min="0.05" max="1"
                value={cond.weight} onChange={e => updateCondition(idx, 'weight', Math.max(0.05, Math.min(1, parseFloat(e.target.value) || 0.1)))} />
            </div>
            <button onClick={() => removeCondition(idx)}
              style={{ padding: '4px 8px', background: 'none', color: '#f87171', border: '1px solid #7f1d1d',
                borderRadius: 4, cursor: 'pointer', fontSize: 12 }}>
              x
            </button>
          </div>
          <div style={{ color: '#6b7280', fontSize: 11, marginTop: 4 }}>{getDescription(cond.indicator)}</div>
        </div>
      ))}
      <button onClick={addCondition}
        style={{ padding: '6px 14px', background: '#1e3a5f', color: '#93c5fd', border: 'none',
          borderRadius: 4, cursor: 'pointer', fontSize: 12, marginTop: 4 }}>
        + Add Condition
      </button>
    </div>
  );
};

// ─── Create Engine Wizard ─────────────────────────────────────────────────────

interface WizardStep {
  step: number;
  name: string;
  schedule: ScheduleOption;
  alpacaApiKey: string;
  alpacaSecretKey: string;
  alpacaEnvironment: 'paper' | 'live';
  policyName: string;
  strategyId: StrategyId;
  direction: 'long' | 'short' | 'auto';
  symbolsInput: string;
  riskAppetite: RiskAppetite;
  cashReservePct: number;
  maxPositionPct: number;
  maxOpenTrades: number;
  maxDailyTrades: number;
  maxDrawdownPct: number;
  maxDailyDrawdownPct: number;
  maxWeeklyDrawdownPct: number;
  scaledEntry: boolean;
  entryTranches: number;
  trailingStopEnabled: boolean;
  profitProtectionEnabled: boolean;
  washSaleProtection: boolean;
  aiMode: AiMode;
  autoExecuteEnabled: boolean;
  autoExecuteThreshold: number;
}

const INITIAL_WIZARD: WizardStep = {
  step: 1,
  name: '',
  schedule: '30min',
  alpacaApiKey: '',
  alpacaSecretKey: '',
  alpacaEnvironment: 'paper',
  policyName: '',
  strategyId: 'momentum',
  direction: 'auto',
  symbolsInput: '',
  riskAppetite: 'moderate',
  cashReservePct: 50,
  maxPositionPct: 10,
  maxOpenTrades: 5,
  maxDailyTrades: 3,
  maxDrawdownPct: 3,
  maxDailyDrawdownPct: 5,
  maxWeeklyDrawdownPct: 10,
  scaledEntry: false,
  entryTranches: 3,
  trailingStopEnabled: true,
  profitProtectionEnabled: true,
  washSaleProtection: false,
  aiMode: 'advisory',
  autoExecuteEnabled: false,
  autoExecuteThreshold: 90,
};

const CreateEngineWizard: React.FC<{
  onCreated: (engine: AlgoEngine) => void;
  onCancel: () => void;
  token: string;
}> = ({ onCreated, onCancel, token }) => {
  const [form, setForm] = useState<WizardStep>(INITIAL_WIZARD);
  const [conditionOverrides, setConditionOverrides] = useState<ConditionOverride[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const set = (key: keyof WizardStep, val: string | number) =>
    setForm(f => ({ ...f, [key]: val }));

  const handleCreate = async () => {
    const symbols = form.symbolsInput.split(/[\s,]+/).map(s => s.toUpperCase().trim()).filter(Boolean);
    if (!form.name.trim()) return setError('Engine name is required');
    if (!form.alpacaApiKey || !form.alpacaSecretKey) return setError('Alpaca credentials are required');
    if (!form.policyName.trim()) return setError('Policy name is required');
    if (symbols.length === 0 || symbols.length > 5) return setError('Enter 1–5 symbols');
    if (form.strategyId === 'custom' && conditionOverrides.length === 0) return setError('Custom strategy requires at least one entry condition');

    const payload: CreateEngineRequest = {
      name: form.name.trim(),
      schedule: form.schedule,
      alpacaApiKey: form.alpacaApiKey,
      alpacaSecretKey: form.alpacaSecretKey,
      alpacaEnvironment: form.alpacaEnvironment,
      policies: [{
        name: form.policyName.trim(),
        strategyId: form.strategyId,
        direction: form.direction,
        symbols,
        riskAppetite: form.riskAppetite,
        ...(conditionOverrides.length > 0 ? { conditionOverrides } : {}),
      }],
      executionRules: {
        cashReservePct: form.cashReservePct,
        maxPositionPct: form.maxPositionPct,
        maxOpenTrades: form.maxOpenTrades,
        maxDailyTrades: form.maxDailyTrades,
        maxDrawdownPct: form.maxDrawdownPct,
        maxDailyDrawdownPct: form.maxDailyDrawdownPct,
        maxWeeklyDrawdownPct: form.maxWeeklyDrawdownPct,
        scaledEntry: form.scaledEntry,
        entryTranches: form.entryTranches,
        trailingStopEnabled: form.trailingStopEnabled,
        profitProtectionEnabled: form.profitProtectionEnabled,
        washSaleProtection: form.washSaleProtection,
      },
      aiMode: form.aiMode,
      autoExecuteEnabled: form.autoExecuteEnabled,
      autoExecuteThreshold: form.autoExecuteThreshold,
    };

    setLoading(true);
    setError(null);
    try {
      const result = await algoService.createEngine(token, payload);
      onCreated(result.engine);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to create engine');
    } finally {
      setLoading(false);
    }
  };

  const inputStyle: React.CSSProperties = {
    width: '100%', padding: '8px 12px', border: '1px solid #374151',
    borderRadius: 6, background: '#111827', color: '#f9fafb',
    fontSize: 14, boxSizing: 'border-box',
  };
  const labelStyle: React.CSSProperties = { fontSize: 13, color: '#9ca3af', marginBottom: 4, display: 'block' };
  const sectionStyle: React.CSSProperties = { marginBottom: 20 };

  return (
    <div style={{ background: '#1f2937', borderRadius: 10, padding: 28, maxWidth: 540, margin: '0 auto' }}>
      <h2 style={{ color: '#f9fafb', marginBottom: 8, fontSize: 20 }}>Create Algo Engine</h2>
      <p style={{ color: '#9ca3af', marginBottom: 24, fontSize: 14 }}>
        Set up an automated trading engine with your strategy and Alpaca credentials.
      </p>

      {error && (
        <div style={{ background: '#7f1d1d', color: '#fca5a5', borderRadius: 6, padding: '10px 14px', marginBottom: 16, fontSize: 14 }}>
          {error}
        </div>
      )}

      <div style={sectionStyle}>
        <label style={labelStyle}>Engine Name</label>
        <input style={inputStyle} placeholder="e.g. My Momentum Engine"
          value={form.name} onChange={e => set('name', e.target.value)} />
      </div>

      <div style={sectionStyle}>
        <label style={labelStyle}>Run Schedule</label>
        <select style={inputStyle} value={form.schedule}
          onChange={e => set('schedule', e.target.value as ScheduleOption)}>
          {Object.entries(SCHEDULE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
      </div>

      <div style={{ background: '#111827', borderRadius: 8, padding: '16px 18px', marginBottom: 20 }}>
        <div style={{ fontSize: 13, color: '#60a5fa', fontWeight: 600, marginBottom: 14 }}>Alpaca Credentials</div>
        <div style={sectionStyle}>
          <label style={labelStyle}>Alpaca Environment</label>
          <select style={inputStyle} value={form.alpacaEnvironment}
            onChange={e => set('alpacaEnvironment', e.target.value)}>
            <option value="paper">Paper Trading (recommended for testing)</option>
            <option value="live">Live Trading</option>
          </select>
        </div>
        <div style={sectionStyle}>
          <label style={labelStyle}>API Key ID</label>
          <input style={inputStyle} placeholder="APCA-API-KEY-ID"
            value={form.alpacaApiKey} onChange={e => set('alpacaApiKey', e.target.value)} />
        </div>
        <div style={{ ...sectionStyle, marginBottom: 0 }}>
          <label style={labelStyle}>API Secret Key</label>
          <input style={inputStyle} type="password" placeholder="APCA-API-SECRET-KEY"
            value={form.alpacaSecretKey} onChange={e => set('alpacaSecretKey', e.target.value)} />
        </div>
      </div>

      <div style={{ background: '#111827', borderRadius: 8, padding: '16px 18px', marginBottom: 20 }}>
        <div style={{ fontSize: 13, color: '#a78bfa', fontWeight: 600, marginBottom: 14 }}>Policy Configuration</div>
        <div style={sectionStyle}>
          <label style={labelStyle}>Policy Name</label>
          <input style={inputStyle} placeholder="e.g. Tech Momentum"
            value={form.policyName} onChange={e => set('policyName', e.target.value)} />
        </div>
        <div style={sectionStyle}>
          <label style={labelStyle}>Strategy</label>
          <select style={inputStyle} value={form.strategyId}
            onChange={e => set('strategyId', e.target.value as StrategyId)}>
            {Object.entries(STRATEGIES).map(([k, v]) => (
              <option key={k} value={k}>{v.icon} {v.name} — {v.description}</option>
            ))}
          </select>
        </div>
        <div style={sectionStyle}>
          <label style={labelStyle}>Direction</label>
          <select style={inputStyle} value={form.direction}
            onChange={e => set('direction', e.target.value)}>
            <option value="auto">Auto — system picks best direction per stock</option>
            <option value="long">Long Only — buy signals only</option>
            <option value="short">Short Only — sell short signals only</option>
          </select>
          <div style={{ color: '#6b7280', fontSize: 11, marginTop: 2 }}>
            {form.strategyId === 'custom' ? 'Direction determines how entry/stop/target levels are calculated' : 'Overrides the strategy\'s built-in direction'}
          </div>
        </div>
        <div style={sectionStyle}>
          <label style={labelStyle}>Symbols (max 5, comma-separated)</label>
          <input style={inputStyle} placeholder="e.g. AAPL, MSFT, NVDA"
            value={form.symbolsInput} onChange={e => set('symbolsInput', e.target.value)} />
        </div>
        <div style={{ ...sectionStyle, marginBottom: 0 }}>
          <label style={labelStyle}>Risk Appetite</label>
          <select style={inputStyle} value={form.riskAppetite}
            onChange={e => set('riskAppetite', e.target.value as RiskAppetite)}>
            {Object.entries(RISK_LABELS).map(([k, v]) => (
              <option key={k} value={k}>{v.label} — {v.description}</option>
            ))}
          </select>
        </div>
      </div>

      <div style={{ background: '#111827', borderRadius: 8, padding: '16px 18px', marginBottom: 20 }}>
        <div style={{ fontSize: 13, color: '#34d399', fontWeight: 600, marginBottom: 6 }}>
          {form.strategyId === 'custom' ? 'Entry Conditions (required)' : 'Condition Overrides (optional)'}
        </div>
        <div style={{ color: '#6b7280', fontSize: 11, marginBottom: 14 }}>
          {form.strategyId === 'custom'
            ? 'Define the entry conditions for your custom strategy. Use technicals, fundamentals, market conditions, and sentiment.'
            : 'Add or override entry conditions on top of the base strategy. These are merged with the strategy\'s built-in conditions.'}
        </div>
        <ConditionEditor conditions={conditionOverrides} onChange={setConditionOverrides} isCustomStrategy={form.strategyId === 'custom'}
          onDirectionChange={(dir) => set('direction', dir)} />
      </div>

      <div style={{ background: '#111827', borderRadius: 8, padding: '16px 18px', marginBottom: 20 }}>
        <div style={{ fontSize: 13, color: '#f59e0b', fontWeight: 600, marginBottom: 14 }}>Execution Rules</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          <div>
            <label style={labelStyle}>Cash Reserve %</label>
            <input style={inputStyle} type="number" min={0} max={90} value={form.cashReservePct}
              onChange={e => set('cashReservePct', Math.max(0, Math.min(90, parseInt(e.target.value) || 0)))} />
            <div style={{ color: '#6b7280', fontSize: 11, marginTop: 2 }}>Keep this % of equity in cash</div>
          </div>
          <div>
            <label style={labelStyle}>Max Position %</label>
            <input style={inputStyle} type="number" min={1} max={100} value={form.maxPositionPct}
              onChange={e => set('maxPositionPct', Math.max(1, Math.min(100, parseInt(e.target.value) || 1)))} />
            <div style={{ color: '#6b7280', fontSize: 11, marginTop: 2 }}>Max single position as % of equity</div>
          </div>
          <div>
            <label style={labelStyle}>Max Open Trades</label>
            <input style={inputStyle} type="number" min={1} max={50} value={form.maxOpenTrades}
              onChange={e => set('maxOpenTrades', Math.max(1, Math.min(50, parseInt(e.target.value) || 1)))} />
          </div>
          <div>
            <label style={labelStyle}>Max Daily Trades</label>
            <input style={inputStyle} type="number" min={1} max={50} value={form.maxDailyTrades}
              onChange={e => set('maxDailyTrades', Math.max(1, Math.min(50, parseInt(e.target.value) || 1)))} />
          </div>
          <div>
            <label style={labelStyle}>Max Drawdown % (per trade)</label>
            <input style={inputStyle} type="number" min={1} max={10} step={0.5} value={form.maxDrawdownPct}
              onChange={e => set('maxDrawdownPct', Math.max(1, Math.min(10, parseFloat(e.target.value) || 3)))} />
            <div style={{ color: '#6b7280', fontSize: 11, marginTop: 2 }}>Force-close a trade if loss exceeds this %</div>
          </div>
          <div>
            <label style={labelStyle}>Max Daily Drawdown %</label>
            <input style={inputStyle} type="number" min={1} max={25} step={1} value={form.maxDailyDrawdownPct}
              onChange={e => set('maxDailyDrawdownPct', Math.max(1, Math.min(25, parseInt(e.target.value) || 5)))} />
            <div style={{ color: '#6b7280', fontSize: 11, marginTop: 2 }}>Auto-pause engine if daily losses exceed this % of equity</div>
          </div>
          <div>
            <label style={labelStyle}>Max Weekly Drawdown %</label>
            <input style={inputStyle} type="number" min={1} max={50} step={1} value={form.maxWeeklyDrawdownPct}
              onChange={e => set('maxWeeklyDrawdownPct', Math.max(1, Math.min(50, parseInt(e.target.value) || 10)))} />
            <div style={{ color: '#6b7280', fontSize: 11, marginTop: 2 }}>Auto-pause engine if weekly losses exceed this % of equity</div>
          </div>
          <div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', marginTop: 6 }}>
              <input type="checkbox" checked={form.scaledEntry}
                onChange={e => setForm(f => ({ ...f, scaledEntry: e.target.checked }))}
                style={{ width: 14, height: 14 }} />
              <span style={{ color: '#d1d5db', fontSize: 12 }}>Scaled Entry (Tranches)</span>
            </label>
            <div style={{ color: '#6b7280', fontSize: 11, marginTop: 2 }}>Scale into positions gradually instead of all at once</div>
            {form.scaledEntry && (
              <div style={{ marginTop: 6, marginLeft: 22 }}>
                <select style={{ padding: '4px 8px', border: '1px solid #374151', borderRadius: 4, background: '#1f2937', color: '#f9fafb', fontSize: 12 }}
                  value={form.entryTranches} onChange={e => set('entryTranches', parseInt(e.target.value))}>
                  <option value={2}>2 tranches (60% + 40%)</option>
                  <option value={3}>3 tranches (40% + 30% + 30%)</option>
                </select>
                <div style={{ color: '#6b7280', fontSize: 10, marginTop: 3 }}>
                  Tranche 2 confirms at 0.5x ATR move in your favor. Tranche 3 at 1.0x ATR. Cancels if price moves 0.5x ATR against.
                </div>
              </div>
            )}
          </div>
          <div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', marginTop: 6 }}>
              <input type="checkbox" checked={form.trailingStopEnabled}
                onChange={e => setForm(f => ({ ...f, trailingStopEnabled: e.target.checked }))}
                style={{ width: 14, height: 14 }} />
              <span style={{ color: '#d1d5db', fontSize: 12 }}>Trailing Stop</span>
            </label>
            <div style={{ color: '#6b7280', fontSize: 11, marginTop: 2 }}>ATR-based trailing stop after consecutive gains</div>
          </div>
          <div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', marginTop: 6 }}>
              <input type="checkbox" checked={form.profitProtectionEnabled}
                onChange={e => setForm(f => ({ ...f, profitProtectionEnabled: e.target.checked }))}
                style={{ width: 14, height: 14 }} />
              <span style={{ color: '#d1d5db', fontSize: 12 }}>Profit Protection</span>
            </label>
            <div style={{ color: '#6b7280', fontSize: 11, marginTop: 2 }}>Close profitable trades on market reversal readings</div>
          </div>
          <div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', marginTop: 6 }}>
              <input type="checkbox" checked={form.washSaleProtection}
                onChange={e => setForm(f => ({ ...f, washSaleProtection: e.target.checked }))}
                style={{ width: 14, height: 14 }} />
              <span style={{ color: '#d1d5db', fontSize: 12 }}>Wash Sale Protection</span>
            </label>
            <div style={{ color: '#6b7280', fontSize: 11, marginTop: 2 }}>Block re-trading symbols closed at a loss within 30 days (IRS wash sale window)</div>
          </div>
        </div>
      </div>

      <div style={{ background: '#111827', borderRadius: 8, padding: '16px 18px', marginBottom: 20 }}>
        <div style={{ fontSize: 13, color: '#818cf8', fontWeight: 600, marginBottom: 14 }}>AI Review &amp; Auto-Execution</div>
        <div style={{ marginBottom: 14 }}>
          <label style={labelStyle}>AI Review Mode</label>
          <select value={form.aiMode}
            onChange={e => setForm(f => ({ ...f, aiMode: e.target.value as AiMode }))}
            style={inputStyle}>
            <option value="off">Off — skip AI review (cheapest, fastest)</option>
            <option value="advisory">Advisory — AI runs but never blocks (recommended)</option>
            <option value="strict">Strict — AI must say EXECUTE or trade is dismissed</option>
          </select>
          <div style={{ color: '#6b7280', fontSize: 11, marginTop: 4 }}>
            In <b>advisory</b> mode the AI's sizing adjustment still scales the position (e.g., HOLD → smaller).
            In <b>strict</b> mode the AI can veto trades — useful for cautious accounts but blocks picks when conditions are stretched.
          </div>
        </div>
        {(
          <>
            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
                <input type="checkbox" checked={form.autoExecuteEnabled}
                  onChange={e => setForm(f => ({ ...f, autoExecuteEnabled: e.target.checked }))}
                  style={{ width: 16, height: 16 }} />
                <span style={{ color: '#d1d5db', fontSize: 13 }}>Enable Auto-Execution</span>
              </label>
              <div style={{ color: '#6b7280', fontSize: 11, marginTop: 4, marginLeft: 26 }}>
                Place orders automatically when policy score (and AI in strict mode) meet the threshold.
              </div>
            </div>
            {form.autoExecuteEnabled && (
              <div>
                <label style={labelStyle}>Auto-Execute Threshold</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <input type="range" min={25} max={100} value={form.autoExecuteThreshold}
                    onChange={e => set('autoExecuteThreshold', parseInt(e.target.value))}
                    style={{ flex: 1 }} />
                  <span style={{ color: '#f9fafb', fontWeight: 700, fontSize: 15, minWidth: 40 }}>
                    {form.autoExecuteThreshold}
                  </span>
                </div>
                <div style={{ color: '#6b7280', fontSize: 11, marginTop: 2 }}>
                  Both policy score AND Claude confidence must be at or above this value
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <div style={{ display: 'flex', gap: 12 }}>
        <button
          onClick={handleCreate}
          disabled={loading}
          style={{ flex: 1, padding: '10px 0', background: '#2563eb', color: '#fff',
            border: 'none', borderRadius: 6, cursor: loading ? 'not-allowed' : 'pointer',
            fontWeight: 600, fontSize: 14 }}
        >
          {loading ? 'Validating & Creating...' : 'Create Engine'}
        </button>
        <button
          onClick={onCancel}
          style={{ padding: '10px 20px', background: '#374151', color: '#d1d5db',
            border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 14 }}
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

// ─── Edit Engine Modal ────────────────────────────────────────────────────────

interface EditForm {
  name: string;
  schedule: ScheduleOption;
  policyName: string;
  strategyId: StrategyId;
  direction: 'long' | 'short' | 'auto';
  symbolsInput: string;
  riskAppetite: RiskAppetite;
  cashReservePct: number;
  maxPositionPct: number;
  maxOpenTrades: number;
  maxDailyTrades: number;
  maxDrawdownPct: number;
  maxDailyDrawdownPct: number;
  maxWeeklyDrawdownPct: number;
  scaledEntry: boolean;
  entryTranches: number;
  trailingStopEnabled: boolean;
  profitProtectionEnabled: boolean;
  washSaleProtection: boolean;
  aiMode: AiMode;
  autoExecuteEnabled: boolean;
  autoExecuteThreshold: number;
}

const EditEngineModal: React.FC<{
  engine: AlgoEngine;
  onSaved: (updated: AlgoEngine) => void;
  onCancel: () => void;
  token: string;
}> = ({ engine, onSaved, onCancel, token }) => {
  const policy = engine.policies[0];
  const [form, setForm] = useState<EditForm>({
    name: engine.name,
    schedule: engine.schedule,
    policyName: policy?.name || '',
    strategyId: policy?.strategyId || 'momentum',
    direction: (policy as any)?.direction || 'auto',
    symbolsInput: policy?.symbols.join(', ') || '',
    riskAppetite: policy?.riskAppetite || 'moderate',
    cashReservePct: engine.executionRules?.cashReservePct ?? 50,
    maxPositionPct: engine.executionRules?.maxPositionPct ?? 10,
    maxOpenTrades: engine.executionRules?.maxOpenTrades ?? 5,
    maxDailyTrades: engine.executionRules?.maxDailyTrades ?? 3,
    maxDrawdownPct: engine.executionRules?.maxDrawdownPct ?? 3,
    maxDailyDrawdownPct: engine.executionRules?.maxDailyDrawdownPct ?? 5,
    maxWeeklyDrawdownPct: engine.executionRules?.maxWeeklyDrawdownPct ?? 10,
    scaledEntry: engine.executionRules?.scaledEntry ?? false,
    entryTranches: engine.executionRules?.entryTranches ?? 3,
    trailingStopEnabled: engine.executionRules?.trailingStopEnabled ?? true,
    profitProtectionEnabled: engine.executionRules?.profitProtectionEnabled ?? true,
    washSaleProtection: engine.executionRules?.washSaleProtection ?? false,
    aiMode: (engine.aiMode || (engine.claudeEnabled ? 'strict' : 'off')) as AiMode,
    autoExecuteEnabled: engine.autoExecuteEnabled ?? false,
    autoExecuteThreshold: engine.autoExecuteThreshold ?? 90,
  });
  const [conditionOverrides, setConditionOverrides] = useState<ConditionOverride[]>(
    policy?.conditionOverrides || []
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const set = (key: keyof EditForm, val: string | number | boolean) =>
    setForm(f => ({ ...f, [key]: val }));

  const handleSave = async () => {
    const symbols = form.symbolsInput.split(/[\s,]+/).map(s => s.toUpperCase().trim()).filter(Boolean);
    if (!form.name.trim()) return setError('Engine name is required');
    if (!form.policyName.trim()) return setError('Policy name is required');
    if (symbols.length === 0 || symbols.length > 5) return setError('Enter 1-5 symbols');

    const updates: Record<string, unknown> = {
      name: form.name.trim(),
      schedule: form.schedule,
      policies: engine.policies.map((p, i) =>
        i === 0
          ? { ...p, name: form.policyName.trim(), strategyId: form.strategyId, direction: form.direction,
              symbols, riskAppetite: form.riskAppetite,
              conditionOverrides: conditionOverrides.length > 0 ? conditionOverrides : undefined }
          : p
      ),
      executionRules: {
        cashReservePct: form.cashReservePct,
        maxPositionPct: form.maxPositionPct,
        maxOpenTrades: form.maxOpenTrades,
        maxDailyTrades: form.maxDailyTrades,
        maxDrawdownPct: form.maxDrawdownPct,
        maxDailyDrawdownPct: form.maxDailyDrawdownPct,
        maxWeeklyDrawdownPct: form.maxWeeklyDrawdownPct,
        scaledEntry: form.scaledEntry,
        entryTranches: form.entryTranches,
        trailingStopEnabled: form.trailingStopEnabled,
        profitProtectionEnabled: form.profitProtectionEnabled,
        washSaleProtection: form.washSaleProtection,
      },
      aiMode: form.aiMode,
      autoExecuteEnabled: form.autoExecuteEnabled,
      autoExecuteThreshold: form.autoExecuteThreshold,
    };

    setLoading(true);
    setError(null);
    try {
      await algoService.updateEngine(token, engine.engineId, updates as any);
      onSaved({
        ...engine,
        name: form.name.trim(),
        schedule: form.schedule,
        policies: updates.policies as AlgoPolicy[],
        executionRules: updates.executionRules as ExecutionRules,
        aiMode: form.aiMode,
        claudeEnabled: form.aiMode !== 'off',
        autoExecuteEnabled: form.autoExecuteEnabled,
        autoExecuteThreshold: form.autoExecuteThreshold,
        updatedAt: new Date().toISOString(),
      });
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update engine');
    } finally {
      setLoading(false);
    }
  };

  const inputStyle: React.CSSProperties = {
    width: '100%', padding: '8px 12px', border: '1px solid #374151',
    borderRadius: 6, background: '#111827', color: '#f9fafb',
    fontSize: 14, boxSizing: 'border-box',
  };
  const labelStyle: React.CSSProperties = { fontSize: 13, color: '#9ca3af', marginBottom: 4, display: 'block' };
  const sectionStyle: React.CSSProperties = { marginBottom: 20 };

  return (
    <div style={{ background: '#1f2937', borderRadius: 10, padding: 28, maxWidth: 540, margin: '0 auto', border: '1px solid #4b5563' }}>
      <h2 style={{ color: '#f9fafb', marginBottom: 8, fontSize: 20 }}>Edit Engine</h2>
      <p style={{ color: '#6b7280', marginBottom: 20, fontSize: 13 }}>
        Alpaca credentials cannot be changed here. Delete and recreate the engine to use different credentials.
      </p>

      {error && (
        <div style={{ background: '#7f1d1d', color: '#fca5a5', borderRadius: 6, padding: '10px 14px', marginBottom: 16, fontSize: 14 }}>
          {error}
        </div>
      )}

      <div style={sectionStyle}>
        <label style={labelStyle}>Engine Name</label>
        <input style={inputStyle} value={form.name} onChange={e => set('name', e.target.value)} />
      </div>

      <div style={sectionStyle}>
        <label style={labelStyle}>Run Schedule</label>
        <select style={inputStyle} value={form.schedule}
          onChange={e => set('schedule', e.target.value as ScheduleOption)}>
          {Object.entries(SCHEDULE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
      </div>

      <div style={{ background: '#111827', borderRadius: 8, padding: '16px 18px', marginBottom: 20 }}>
        <div style={{ fontSize: 13, color: '#a78bfa', fontWeight: 600, marginBottom: 14 }}>Policy Configuration</div>
        <div style={sectionStyle}>
          <label style={labelStyle}>Policy Name</label>
          <input style={inputStyle} value={form.policyName} onChange={e => set('policyName', e.target.value)} />
        </div>
        <div style={sectionStyle}>
          <label style={labelStyle}>Strategy</label>
          <select style={inputStyle} value={form.strategyId}
            onChange={e => set('strategyId', e.target.value as StrategyId)}>
            {Object.entries(STRATEGIES).map(([k, v]) => (
              <option key={k} value={k}>{v.icon} {v.name} — {v.description}</option>
            ))}
          </select>
        </div>
        <div style={sectionStyle}>
          <label style={labelStyle}>Direction</label>
          <select style={inputStyle} value={form.direction}
            onChange={e => set('direction', e.target.value)}>
            <option value="auto">Auto — system picks best direction per stock</option>
            <option value="long">Long Only — buy signals only</option>
            <option value="short">Short Only — sell short signals only</option>
          </select>
        </div>
        <div style={sectionStyle}>
          <label style={labelStyle}>Symbols (max 5, comma-separated)</label>
          <input style={inputStyle} value={form.symbolsInput} onChange={e => set('symbolsInput', e.target.value)} />
        </div>
        <div style={{ ...sectionStyle, marginBottom: 0 }}>
          <label style={labelStyle}>Risk Appetite</label>
          <select style={inputStyle} value={form.riskAppetite}
            onChange={e => set('riskAppetite', e.target.value as RiskAppetite)}>
            {Object.entries(RISK_LABELS).map(([k, v]) => (
              <option key={k} value={k}>{v.label} — {v.description}</option>
            ))}
          </select>
        </div>
      </div>

      <div style={{ background: '#111827', borderRadius: 8, padding: '16px 18px', marginBottom: 20 }}>
        <div style={{ fontSize: 13, color: '#34d399', fontWeight: 600, marginBottom: 6 }}>
          {form.strategyId === 'custom' ? 'Entry Conditions (required)' : 'Condition Overrides (optional)'}
        </div>
        <div style={{ color: '#6b7280', fontSize: 11, marginBottom: 14 }}>
          {form.strategyId === 'custom'
            ? 'Define the entry conditions for your custom strategy. Use technicals, fundamentals, market conditions, and sentiment.'
            : 'Add or override entry conditions on top of the base strategy. These are merged with the strategy\'s built-in conditions.'}
        </div>
        <ConditionEditor conditions={conditionOverrides} onChange={setConditionOverrides} isCustomStrategy={form.strategyId === 'custom'}
          onDirectionChange={(dir) => set('direction', dir)} />
      </div>

      <div style={{ background: '#111827', borderRadius: 8, padding: '16px 18px', marginBottom: 20 }}>
        <div style={{ fontSize: 13, color: '#f59e0b', fontWeight: 600, marginBottom: 14 }}>Execution Rules</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          <div>
            <label style={labelStyle}>Cash Reserve %</label>
            <input style={inputStyle} type="number" min={0} max={90} value={form.cashReservePct}
              onChange={e => set('cashReservePct', Math.max(0, Math.min(90, parseInt(e.target.value) || 0)))} />
            <div style={{ color: '#6b7280', fontSize: 11, marginTop: 2 }}>Keep this % of equity in cash</div>
          </div>
          <div>
            <label style={labelStyle}>Max Position %</label>
            <input style={inputStyle} type="number" min={1} max={100} value={form.maxPositionPct}
              onChange={e => set('maxPositionPct', Math.max(1, Math.min(100, parseInt(e.target.value) || 1)))} />
            <div style={{ color: '#6b7280', fontSize: 11, marginTop: 2 }}>Max single position as % of equity</div>
          </div>
          <div>
            <label style={labelStyle}>Max Open Trades</label>
            <input style={inputStyle} type="number" min={1} max={50} value={form.maxOpenTrades}
              onChange={e => set('maxOpenTrades', Math.max(1, Math.min(50, parseInt(e.target.value) || 1)))} />
          </div>
          <div>
            <label style={labelStyle}>Max Daily Trades</label>
            <input style={inputStyle} type="number" min={1} max={50} value={form.maxDailyTrades}
              onChange={e => set('maxDailyTrades', Math.max(1, Math.min(50, parseInt(e.target.value) || 1)))} />
          </div>
          <div>
            <label style={labelStyle}>Max Drawdown % (per trade)</label>
            <input style={inputStyle} type="number" min={1} max={10} step={0.5} value={form.maxDrawdownPct}
              onChange={e => set('maxDrawdownPct', Math.max(1, Math.min(10, parseFloat(e.target.value) || 3)))} />
            <div style={{ color: '#6b7280', fontSize: 11, marginTop: 2 }}>Force-close a trade if loss exceeds this %</div>
          </div>
          <div>
            <label style={labelStyle}>Max Daily Drawdown %</label>
            <input style={inputStyle} type="number" min={1} max={25} step={1} value={form.maxDailyDrawdownPct}
              onChange={e => set('maxDailyDrawdownPct', Math.max(1, Math.min(25, parseInt(e.target.value) || 5)))} />
            <div style={{ color: '#6b7280', fontSize: 11, marginTop: 2 }}>Auto-pause engine if daily losses exceed this % of equity</div>
          </div>
          <div>
            <label style={labelStyle}>Max Weekly Drawdown %</label>
            <input style={inputStyle} type="number" min={1} max={50} step={1} value={form.maxWeeklyDrawdownPct}
              onChange={e => set('maxWeeklyDrawdownPct', Math.max(1, Math.min(50, parseInt(e.target.value) || 10)))} />
            <div style={{ color: '#6b7280', fontSize: 11, marginTop: 2 }}>Auto-pause engine if weekly losses exceed this % of equity</div>
          </div>
          <div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', marginTop: 6 }}>
              <input type="checkbox" checked={form.scaledEntry}
                onChange={e => setForm(f => ({ ...f, scaledEntry: e.target.checked }))}
                style={{ width: 14, height: 14 }} />
              <span style={{ color: '#d1d5db', fontSize: 12 }}>Scaled Entry (Tranches)</span>
            </label>
            <div style={{ color: '#6b7280', fontSize: 11, marginTop: 2 }}>Scale into positions gradually instead of all at once</div>
            {form.scaledEntry && (
              <div style={{ marginTop: 6, marginLeft: 22 }}>
                <select style={{ padding: '4px 8px', border: '1px solid #374151', borderRadius: 4, background: '#1f2937', color: '#f9fafb', fontSize: 12 }}
                  value={form.entryTranches} onChange={e => set('entryTranches', parseInt(e.target.value))}>
                  <option value={2}>2 tranches (60% + 40%)</option>
                  <option value={3}>3 tranches (40% + 30% + 30%)</option>
                </select>
                <div style={{ color: '#6b7280', fontSize: 10, marginTop: 3 }}>
                  Tranche 2 confirms at 0.5x ATR move in your favor. Tranche 3 at 1.0x ATR. Cancels if price moves 0.5x ATR against.
                </div>
              </div>
            )}
          </div>
          <div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', marginTop: 6 }}>
              <input type="checkbox" checked={form.trailingStopEnabled}
                onChange={e => setForm(f => ({ ...f, trailingStopEnabled: e.target.checked }))}
                style={{ width: 14, height: 14 }} />
              <span style={{ color: '#d1d5db', fontSize: 12 }}>Trailing Stop</span>
            </label>
            <div style={{ color: '#6b7280', fontSize: 11, marginTop: 2 }}>ATR-based trailing stop after consecutive gains</div>
          </div>
          <div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', marginTop: 6 }}>
              <input type="checkbox" checked={form.profitProtectionEnabled}
                onChange={e => setForm(f => ({ ...f, profitProtectionEnabled: e.target.checked }))}
                style={{ width: 14, height: 14 }} />
              <span style={{ color: '#d1d5db', fontSize: 12 }}>Profit Protection</span>
            </label>
            <div style={{ color: '#6b7280', fontSize: 11, marginTop: 2 }}>Close profitable trades on market reversal readings</div>
          </div>
          <div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', marginTop: 6 }}>
              <input type="checkbox" checked={form.washSaleProtection}
                onChange={e => setForm(f => ({ ...f, washSaleProtection: e.target.checked }))}
                style={{ width: 14, height: 14 }} />
              <span style={{ color: '#d1d5db', fontSize: 12 }}>Wash Sale Protection</span>
            </label>
            <div style={{ color: '#6b7280', fontSize: 11, marginTop: 2 }}>Block re-trading symbols closed at a loss within 30 days (IRS wash sale window)</div>
          </div>
        </div>
      </div>

      <div style={{ background: '#111827', borderRadius: 8, padding: '16px 18px', marginBottom: 20 }}>
        <div style={{ fontSize: 13, color: '#818cf8', fontWeight: 600, marginBottom: 14 }}>AI Review &amp; Auto-Execution</div>
        <div style={{ marginBottom: 14 }}>
          <label style={labelStyle}>AI Review Mode</label>
          <select value={form.aiMode}
            onChange={e => setForm(f => ({ ...f, aiMode: e.target.value as AiMode }))}
            style={inputStyle}>
            <option value="off">Off — skip AI review (cheapest, fastest)</option>
            <option value="advisory">Advisory — AI runs but never blocks (recommended)</option>
            <option value="strict">Strict — AI must say EXECUTE or trade is dismissed</option>
          </select>
          <div style={{ color: '#6b7280', fontSize: 11, marginTop: 4 }}>
            In <b>advisory</b> mode the AI's sizing adjustment still scales the position.
            In <b>strict</b> mode the AI can veto trades.
          </div>
        </div>
        <div style={{ marginBottom: 14 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
            <input type="checkbox" checked={form.autoExecuteEnabled}
              onChange={e => setForm(f => ({ ...f, autoExecuteEnabled: e.target.checked }))}
              style={{ width: 16, height: 16 }} />
            <span style={{ color: '#d1d5db', fontSize: 13 }}>Enable Auto-Execution</span>
          </label>
        </div>
        {form.autoExecuteEnabled && (
          <div>
            <label style={labelStyle}>Auto-Execute Threshold</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <input type="range" min={25} max={100} value={form.autoExecuteThreshold}
                onChange={e => set('autoExecuteThreshold', parseInt(e.target.value))}
                style={{ flex: 1 }} />
              <span style={{ color: '#f9fafb', fontWeight: 700, fontSize: 15, minWidth: 40 }}>
                {form.autoExecuteThreshold}
              </span>
            </div>
          </div>
        )}
      </div>

      <div style={{ display: 'flex', gap: 12 }}>
        <button onClick={handleSave} disabled={loading}
          style={{ flex: 1, padding: '10px 0', background: '#2563eb', color: '#fff',
            border: 'none', borderRadius: 6, cursor: loading ? 'not-allowed' : 'pointer',
            fontWeight: 600, fontSize: 14 }}>
          {loading ? 'Saving...' : 'Save Changes'}
        </button>
        <button onClick={onCancel}
          style={{ padding: '10px 20px', background: '#374151', color: '#d1d5db',
            border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 14 }}>
          Cancel
        </button>
      </div>
    </div>
  );
};

// ─── Engine Card ──────────────────────────────────────────────────────────────

const EngineCard: React.FC<{
  engine: AlgoEngine;
  onToggle: (engineId: string, newStatus: 'active' | 'paused') => void;
  onDelete: (engineId: string) => void;
  onEdit: (engineId: string) => void;
}> = ({ engine, onToggle, onDelete, onEdit }) => {
  const statusColor = engine.status === 'active' ? '#16a34a' : engine.status === 'paused' ? '#d97706' : '#dc2626';
  const pendingCount = engine.signalSummary?.pendingSignals || 0;

  return (
    <div style={{ background: '#1f2937', borderRadius: 10, padding: '20px 24px', marginBottom: 16, border: '1px solid #374151' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
            <span style={{ color: '#f9fafb', fontWeight: 700, fontSize: 17 }}>{engine.name}</span>
            <span style={{ background: statusColor, color: '#fff', borderRadius: 4, padding: '2px 8px', fontSize: 11, fontWeight: 600 }}>
              {engine.status.toUpperCase()}
            </span>
            <span style={{ background: '#1e3a5f', color: '#93c5fd', borderRadius: 4, padding: '2px 7px', fontSize: 11 }}>
              {engine.alpacaEnvironment}
            </span>
          </div>
          <div style={{ color: '#9ca3af', fontSize: 13 }}>
            {SCHEDULE_LABELS[engine.schedule]} · {engine.policies.length} {engine.policies.length === 1 ? 'policy' : 'policies'}
            {pendingCount > 0 && (
              <span style={{ marginLeft: 10, color: '#fbbf24', fontWeight: 600 }}>
                {pendingCount} pending pick{pendingCount > 1 ? 's' : ''}
              </span>
            )}
          </div>
          {(engine as any).pausedUntil && new Date((engine as any).pausedUntil) > new Date() && (
            <div style={{ marginTop: 6, padding: '6px 12px', background: '#7f1d1d', borderRadius: 6, fontSize: 12, color: '#fca5a5' }}>
              AUTO-PAUSED until {new Date((engine as any).pausedUntil).toLocaleString('en-US', { timeZone: 'America/New_York', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })} ET — {(engine as any).pauseReason || 'drawdown limit exceeded'}
            </div>
          )}
          {(engine as any).dailyPnl != null && (
            <div style={{ marginTop: 4, fontSize: 11, color: '#9ca3af' }}>
              Today: <span style={{ color: (engine as any).dailyPnl >= 0 ? '#4ade80' : '#f87171', fontWeight: 600 }}>${((engine as any).dailyPnl || 0).toFixed(2)}</span>
              {(engine as any).weeklyPnl != null && (
                <> · Week: <span style={{ color: (engine as any).weeklyPnl >= 0 ? '#4ade80' : '#f87171', fontWeight: 600 }}>${((engine as any).weeklyPnl || 0).toFixed(2)}</span></>
              )}
            </div>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            onClick={() => onEdit(engine.engineId)}
            style={{ padding: '6px 14px', background: '#374151', color: '#60a5fa',
              border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}
          >
            Edit
          </button>
          <button
            onClick={() => onToggle(engine.engineId, engine.status === 'active' ? 'paused' : 'active')}
            style={{ padding: '6px 14px', background: engine.status === 'active' ? '#7c3aed' : '#16a34a',
              color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}
          >
            {engine.status === 'active' ? 'Pause' : 'Resume'}
          </button>
          <button
            onClick={() => onDelete(engine.engineId)}
            style={{ padding: '6px 14px', background: '#374151', color: '#f87171',
              border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}
          >
            Delete
          </button>
        </div>
      </div>

      {/* Policies */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {engine.policies.map(policy => (
          <PolicyRow key={policy.policyId} policy={policy} />
        ))}
      </div>

      {/* Execution rules */}
      {engine.executionRules && (
        <div style={{ display: 'flex', gap: 12, marginTop: 10, flexWrap: 'wrap', fontSize: 11, color: '#9ca3af' }}>
          <span>Cash Reserve: <strong style={{ color: '#fbbf24' }}>{engine.executionRules.cashReservePct}%</strong></span>
          <span>Max Position: <strong style={{ color: '#e5e7eb' }}>{engine.executionRules.maxPositionPct}%</strong></span>
          <span>Max Open: <strong style={{ color: '#e5e7eb' }}>{engine.executionRules.maxOpenTrades}</strong></span>
          <span>Max Daily: <strong style={{ color: '#e5e7eb' }}>{engine.executionRules.maxDailyTrades}</strong></span>
          <span>Max DD: <strong style={{ color: '#ef4444' }}>{engine.executionRules.maxDrawdownPct ?? 3}%</strong></span>
          {engine.executionRules.trailingStopEnabled !== false && <span style={{ color: '#34d399' }}>Trailing Stop</span>}
          {engine.executionRules.profitProtectionEnabled !== false && <span style={{ color: '#34d399' }}>Profit Protection</span>}
          {engine.executionRules.scaledEntry && <span style={{ color: '#a78bfa' }}>Scaled Entry ({engine.executionRules.entryTranches || 3} tranches)</span>}
          {engine.executionRules.washSaleProtection && <span style={{ color: '#fbbf24' }}>Wash Sale Protection</span>}
        </div>
      )}

      {/* AI mode + auto-execute status */}
      {(() => {
        const mode = engine.aiMode || (engine.claudeEnabled ? 'strict' : 'off');
        const aiColor = mode === 'strict' ? '#fbbf24' : mode === 'advisory' ? '#818cf8' : '#6b7280';
        return (
          <div style={{ display: 'flex', gap: 12, marginTop: 8, flexWrap: 'wrap', fontSize: 11, color: '#9ca3af' }}>
            <span style={{ color: aiColor }}>AI Review: {mode.toUpperCase()}</span>
            {engine.autoExecuteEnabled && (
              <span>Auto-Execute: <strong style={{ color: '#4ade80' }}>ON</strong> (threshold: {engine.autoExecuteThreshold || 90})</span>
            )}
          </div>
        );
      })()}

      {engine.lastRunAt && (
        <div style={{ marginTop: 12, color: '#6b7280', fontSize: 12 }}>
          Last run: {new Date(engine.lastRunAt).toLocaleString()}
          {engine.signalSummary.lastSignalAt && (
            <span style={{ marginLeft: 12 }}>
              Last pick: {engine.signalSummary.lastSignalSymbol} {engine.signalSummary.lastSignalAction}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

const PolicyRow: React.FC<{ policy: AlgoPolicy }> = ({ policy }) => {
  const strategy = STRATEGIES[policy.strategyId];
  const risk = RISK_LABELS[policy.riskAppetite];

  return (
    <div style={{ background: '#111827', borderRadius: 6, padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
      <span style={{ color: '#e5e7eb', fontWeight: 600, fontSize: 13 }}>{policy.name}</span>
      <span style={{ color: '#a78bfa', fontSize: 12 }}>{strategy?.icon} {strategy?.name}</span>
      <span style={{ background: risk.color + '20', color: risk.color, borderRadius: 4, padding: '1px 6px', fontSize: 11 }}>
        {risk.label}
      </span>
      <span style={{ color: '#6b7280', fontSize: 12 }}>
        {policy.symbols.join(', ')}
      </span>
      {!policy.enabled && (
        <span style={{ color: '#6b7280', fontSize: 11 }}>(disabled)</span>
      )}
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

export const AlgoEngines: React.FC = () => {
  const { user, token } = useAuth();
  const [engines, setEngines] = useState<AlgoEngine[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [editEngineId, setEditEngineId] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  const fetchEngines = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const result = await algoService.getEngines(token);
      setEngines((result.engines || []).filter((e: any) => e.brokerType !== 'tastytrade'));
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load engines');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchEngines(); }, [fetchEngines]);

  const handleToggle = async (engineId: string, newStatus: 'active' | 'paused') => {
    if (!token) return;
    try {
      await algoService.updateEngine(token, engineId, { status: newStatus });
      setEngines(prev => prev.map(e => e.engineId === engineId ? { ...e, status: newStatus } : e));
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to update engine');
    }
  };

  const handleDelete = async (engineId: string) => {
    if (!token) return;
    try {
      await algoService.deleteEngine(token, engineId);
      setEngines(prev => prev.filter(e => e.engineId !== engineId));
      setDeleteConfirm(null);
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to delete engine');
    }
  };

  const algoEnabled = canAccessAutoTrader(user, 'alpaca');
  const engineLimit = user?.algoEngineLimit || 1;
  const atLimit = engines.length >= engineLimit;

  return (
    <div className="dashboard-container">
      <Navigation />
      <div style={{ maxWidth: 800, margin: '0 auto', padding: '24px 16px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
            <div>
              <h1 style={{ color: '#f9fafb', fontSize: 26, fontWeight: 700, margin: 0 }}>
                Algo Trading Engines <span className="beta-badge" style={{ fontSize: 11, marginLeft: 8 }}>BETA</span>
              </h1>
              <p style={{ color: '#9ca3af', fontSize: 14, marginTop: 4 }}>
                Automated strategy engines that generate trade picks on your schedule.
              </p>
            </div>
            {algoEnabled && !showCreate && (
              <button
                onClick={() => setShowCreate(true)}
                disabled={atLimit}
                title={atLimit ? `Engine limit reached (${engineLimit})` : undefined}
                style={{ padding: '10px 20px', background: atLimit ? '#374151' : '#2563eb',
                  color: atLimit ? '#6b7280' : '#fff', border: 'none', borderRadius: 8,
                  cursor: atLimit ? 'not-allowed' : 'pointer', fontWeight: 600, fontSize: 14 }}
              >
                + New Engine
              </button>
            )}
          </div>

          {/* Upgrade prompt */}
          {!algoEnabled && (
            <div style={{ background: '#1f2937', border: '1px solid #374151', borderRadius: 10, padding: 28, textAlign: 'center' }}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>🤖</div>
              <h2 style={{ color: '#f9fafb', marginBottom: 8 }}>Algo Trading is a Pro Feature</h2>
              <p style={{ color: '#9ca3af', marginBottom: 20 }}>
                Connect your Alpaca account, define strategies, and let the engine generate trade picks automatically.
              </p>
              <p style={{ color: '#6b7280', fontSize: 13 }}>
                Contact us to enable Algo Trading on your Pro account.
              </p>
            </div>
          )}

          {/* Create engine wizard */}
          {showCreate && algoEnabled && (
            <div style={{ marginBottom: 28 }}>
              <CreateEngineWizard
                token={token!}
                onCreated={(engine) => { setEngines(prev => [engine, ...prev]); setShowCreate(false); }}
                onCancel={() => setShowCreate(false)}
              />
            </div>
          )}

          {/* At limit notice */}
          {algoEnabled && atLimit && !showCreate && (
            <div style={{ background: '#1c1917', border: '1px solid #78350f', borderRadius: 8, padding: '12px 16px', marginBottom: 16, fontSize: 13, color: '#fbbf24' }}>
              You have reached your engine limit ({engineLimit}). Contact us to add more engines.
            </div>
          )}

          {/* Engine list */}
          {loading ? (
            <div style={{ textAlign: 'center', color: '#9ca3af', padding: 40 }}>Loading engines...</div>
          ) : error ? (
            <div style={{ color: '#f87171', background: '#7f1d1d', borderRadius: 8, padding: 16 }}>{error}</div>
          ) : engines.length === 0 && algoEnabled ? (
            <div style={{ textAlign: 'center', color: '#9ca3af', padding: 40 }}>
              No engines yet. Create your first engine to get started.
            </div>
          ) : (
            engines.map(engine => (
              <React.Fragment key={engine.engineId}>
                {editEngineId === engine.engineId ? (
                  <div style={{ marginBottom: 16 }}>
                    <EditEngineModal
                      engine={engine}
                      token={token!}
                      onSaved={(updated) => {
                        setEngines(prev => prev.map(e => e.engineId === updated.engineId ? updated : e));
                        setEditEngineId(null);
                      }}
                      onCancel={() => setEditEngineId(null)}
                    />
                  </div>
                ) : (
                <>
                <EngineCard
                  engine={engine}
                  onToggle={handleToggle}
                  onDelete={(id) => setDeleteConfirm(id)}
                  onEdit={(id) => setEditEngineId(id)}
                />
                {deleteConfirm === engine.engineId && (
                  <div style={{ background: '#1f2937', border: '1px solid #dc2626', borderRadius: 8, padding: '14px 18px', marginTop: -8, marginBottom: 16 }}>
                    <span style={{ color: '#f87171', fontSize: 14 }}>
                      Delete <strong>{engine.name}</strong>? This removes the engine, its EventBridge rule, and pending picks.
                    </span>
                    <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                      <button onClick={() => handleDelete(engine.engineId)}
                        style={{ padding: '6px 16px', background: '#dc2626', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>
                        Confirm Delete
                      </button>
                      <button onClick={() => setDeleteConfirm(null)}
                        style={{ padding: '6px 16px', background: '#374151', color: '#d1d5db', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
                </>
                )}
              </React.Fragment>
            ))
          )}
      </div>
      <RiskDisclaimer />
    </div>
  );
};

export default AlgoEngines;
